import { useRef, useEffect, useState, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const SLIDE_INTERVAL_MS = 6000;
const SLIDE_COUNT = 5;

// Image picks track the live ryebunkerhill.com homepage rotator:
// exterior / amenities pool (exact filename match) / luxury interior /
// aerial “horizons” / upscale finishes (DSC05276 match).
const slide1ImageDef: SlotGroupDefinition = {
  label: 'Slide 1 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[53].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[53].alt_text',
      defaultValue: 'Rye Bunker Hill apartment building exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 53,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A strong exterior or community photo that reads as modern Texas apartment living.',
    context:
      'Hero slideshow slide 1 of 5. Full-bleed background. Do not reuse slides 2–5.',
  },
};

const slide2ImageDef: SlotGroupDefinition = {
  label: 'Slide 2 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[70].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[70].alt_text',
      defaultValue: 'Resort-style swimming pool and amenities',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 70,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A photo of a standout community amenity at this property.',
    context:
      'Hero slideshow slide 2 of 5. Full-bleed background. Different from slides 1, 3, 4, and 5.',
  },
};

const slide3ImageDef: SlotGroupDefinition = {
  label: 'Slide 3 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      defaultValue: 'Modern luxury apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A photo of a finished apartment interior that feels modern and luxurious.',
    context:
      'Hero slideshow slide 3 of 5. Full-bleed background. Different from slides 1, 2, 4, and 5.',
  },
};

const slide4ImageDef: SlotGroupDefinition = {
  label: 'Slide 4 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[17].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[17].alt_text',
      defaultValue: 'Aerial view of the community and surroundings',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 17,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A wide or aerial photo that suggests space, views, or expanding horizons.',
    context:
      'Hero slideshow slide 4 of 5. Full-bleed background. Different from slides 1, 2, 3, and 5.',
  },
};

const slide5ImageDef: SlotGroupDefinition = {
  label: 'Slide 5 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[26].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[26].alt_text',
      defaultValue: 'Upscale apartment finishes and design details',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 26,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A photo that highlights upscale finishes or trailblazing interior design details.',
    context:
      'Hero slideshow slide 5 of 5. Full-bleed background. Different from slides 1–4.',
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function slideBgClass(active: boolean) {
  return `hero-slide-bg absolute inset-0${active ? ' is-active' : ''}`;
}

const headingTokenClass =
  'hero-entrance hero-entrance-1 hero-title-glow font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight drop-shadow-lg uppercase';

export function Hero() {
  const slide1Image = useSlotGroup('hero.slide_1_image', slide1ImageDef);
  const slide2Image = useSlotGroup('hero.slide_2_image', slide2ImageDef);
  const slide3Image = useSlotGroup('hero.slide_3_image', slide3ImageDef);
  const slide4Image = useSlotGroup('hero.slide_4_image', slide4ImageDef);
  const slide5Image = useSlotGroup('hero.slide_5_image', slide5ImageDef);

  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (reducedMotion || isEditing || paused) return;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % SLIDE_COUNT);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reducedMotion, isEditing, paused]);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (reducedMotion) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [reducedMotion]);

  const goPrev = useCallback(() => {
    setActive((i) => (i - 1 + SLIDE_COUNT) % SLIDE_COUNT);
  }, []);

  const goNext = useCallback(() => {
    setActive((i) => (i + 1) % SLIDE_COUNT);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-screen overflow-hidden"
      aria-roledescription="carousel"
      aria-label="Property highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        ref={imgWrapRef}
        className="absolute h-[140%]"
        style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
      >
        <EditableSlotGroup groupPath="hero.slide_1_image" definition={slide1ImageDef}>
          <div className={slideBgClass(active === 0)} aria-hidden={active !== 0}>
            {slide1Image.image ? (
              <VibesitesImage
                url={slide1Image.image}
                alt=""
                priority
                sizes="100vw"
                className="hero-bg-image w-full h-full object-cover"
                style={{
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="hero.slide_2_image" definition={slide2ImageDef}>
          <div className={slideBgClass(active === 1)} aria-hidden={active !== 1}>
            {slide2Image.image ? (
              <VibesitesImage
                url={slide2Image.image}
                alt=""
                sizes="100vw"
                className="hero-bg-image w-full h-full object-cover"
                style={{
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="hero.slide_3_image" definition={slide3ImageDef}>
          <div className={slideBgClass(active === 2)} aria-hidden={active !== 2}>
            {slide3Image.image ? (
              <VibesitesImage
                url={slide3Image.image}
                alt=""
                sizes="100vw"
                className="hero-bg-image w-full h-full object-cover"
                style={{
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="hero.slide_4_image" definition={slide4ImageDef}>
          <div className={slideBgClass(active === 3)} aria-hidden={active !== 3}>
            {slide4Image.image ? (
              <VibesitesImage
                url={slide4Image.image}
                alt=""
                sizes="100vw"
                className="hero-bg-image w-full h-full object-cover"
                style={{
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="hero.slide_5_image" definition={slide5ImageDef}>
          <div className={slideBgClass(active === 4)} aria-hidden={active !== 4}>
            {slide5Image.image ? (
              <VibesitesImage
                url={slide5Image.image}
                alt=""
                sizes="100vw"
                className="hero-bg-image w-full h-full object-cover"
                style={{
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        </EditableSlotGroup>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/28 via-black/10 to-black/4 pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8 pointer-events-none">
        <div className="w-full max-w-5xl mb-10 pointer-events-auto">
          <RichTextContent
            path="hero.title"
            label="Title"
            defaultValue="<p>Modern Style for the Modern Texan</p>"
            as="h1"
            className={headingTokenClass}
          />
        </div>

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-4 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300 pointer-events-auto"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <button
        type="button"
        className="hero-arrow hero-arrow--prev"
        aria-label="Previous slide"
        onClick={goPrev}
      >
        <svg viewBox="0 0 12 24" aria-hidden="true" focusable="false">
          <path d="M10 2L2 12l8 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        className="hero-arrow hero-arrow--next"
        aria-label="Next slide"
        onClick={goNext}
      >
        <svg viewBox="0 0 12 24" aria-hidden="true" focusable="false">
          <path d="M2 2l8 10-8 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <style>{`
        .hero-slide-bg {
          opacity: 0;
          transition: opacity 1.1s cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }
        .hero-slide-bg.is-active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }
        @keyframes heroKenBurns {
          0% { transform: scale(1.06) translate(0, 0); }
          100% { transform: scale(1.12) translate(-1%, -0.6%); }
        }
        .hero-slide-bg.is-active .hero-bg-image {
          animation: heroKenBurns 28s ease-in-out infinite alternate;
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.25s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        .hero-cta {
          background: rgba(0, 0, 0, 0.32);
          border: 1.5px solid rgba(255, 255, 255, 0.75);
          border-radius: 2px;
          color: #fff;
          box-shadow: none;
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0);
          transition: background 0.3s ease;
        }
        .hero-cta:hover {
          background: rgba(0, 0, 0, 0.32);
          border-color: #fff;
          box-shadow: none;
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          background: rgba(255, 255, 255, 0.12);
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .hero-title-glow {
          text-shadow:
            0 2px 6px rgba(0, 0, 0, 0.75),
            0 4px 18px rgba(0, 0, 0, 0.55),
            0 8px 36px rgba(0, 0, 0, 0.4);
        }
        .hero-arrow {
          position: absolute;
          top: 50%;
          z-index: 20;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 3.75rem;
          padding: 0;
          border: none;
          border-radius: 0;
          background: rgba(0, 0, 0, 0.45);
          color: #fff;
          cursor: pointer;
          transition: background 0.25s ease;
        }
        .hero-arrow--prev { left: 0; }
        .hero-arrow--next { right: 0; }
        .hero-arrow:hover {
          background: rgba(0, 0, 0, 0.65);
        }
        .hero-arrow:focus-visible {
          outline: 2px solid #fff;
          outline-offset: -2px;
        }
        .hero-arrow svg {
          width: 0.7rem;
          height: 1.35rem;
        }
        @media (max-width: 640px) {
          .hero-arrow {
            width: 1.85rem;
            height: 3.1rem;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-slide-bg {
            transition: none;
          }
          .hero-slide-bg.is-active .hero-bg-image { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Modern Style for the Modern Texan</p>',
    },
  },
  slotGroups: {
    'hero.slide_1_image': slide1ImageDef,
    'hero.slide_2_image': slide2ImageDef,
    'hero.slide_3_image': slide3ImageDef,
    'hero.slide_4_image': slide4ImageDef,
    'hero.slide_5_image': slide5ImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
